import { HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, of, Subscription } from 'rxjs';
import { catchError, debounceTime, finalize, groupBy, map, mergeMap, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { FormatoDigital } from '../../../domain/formato-digital.domain';
import { Material } from '../../../domain/material.domain';
import { Programa } from '../../../domain/programa.domain';
import { TipoProducto } from '../../../domain/tipo-producto.domain';
import { UbicacionDigital } from '../../../domain/ubicacion-digital.domain';
import { FormatoDigitalService } from '../../../services/formato-digital/formato-digital.service';
import { MaterialService } from '../../../services/material/material.service';
import { ProgramaService } from '../../../services/programa/programa.service';
import { TipoMaterialService } from '../../../services/tipo-material/tipo-material.service';
import { UbicacionDigitalService } from '../../../services/ubicacion-digital/ubicacion-digital.service';
import { selectedEntityValidator } from '../../../utilities/validators.util';
import { resolveSubtotalFinal } from '../../../services/pricing/pricing-override';

type ItemTipoCodigo = 'AIRE_SPOT' | 'WEB_BANNER' | 'REDES_PIEZA' | 'PUBLINOTA' | 'COBERTURA_EVENTO' | 'PNT';
type ItemsControl = { group: FormGroup; index: number };
type LookupField = 'programa' | 'material' | 'formatoDigital' | 'ubicacionDigital';

type LookupRequest<T> = {
  query: string;
  rowIndex: number;
  group: FormGroup;
  field: LookupField;
  mapResponse: (resp: any) => T[];
  fetch: () => ReturnType<ProgramaService['searchProgramas']>;
  suggestionsSignal: ReturnType<typeof signal<T[]>>;
};

@Injectable()
export class ItemsEmisionFacadeService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private itemsFormArray: FormArray | null = null;
  private formArraySubscription: Subscription | null = null;
  private tipoProductoOptions: TipoProducto[] = [];
  private lastSelections: Partial<Record<ItemTipoCodigo, any>> = {};
  private rowIdCounter = 0;
  private deletedItemIds = new Set<number>();
  private readonly requiredFieldsByTipo: Record<ItemTipoCodigo, string[]> = {
    AIRE_SPOT: ['programa', 'diaTipo', 'frecuencia', 'horaDesde', 'horaHasta', 'duracionSegundos', 'material', 'avisos'],
    WEB_BANNER: ['ubicacionDigital', 'formatoDigital', 'fechaDesde', 'fechaHasta', 'cantidad'],
    REDES_PIEZA: ['plataformaRedes', 'formatoDigital', 'cantidad'],
    PUBLINOTA: ['tituloContenido', 'cantidad'],
    COBERTURA_EVENTO: ['eventoNombre', 'fechaEvento', 'cantidad'],
    PNT: ['textoALeer', 'cantidad']
  };
  private readonly requiredFieldLabels: Record<string, string> = {
    programa: 'Programa',
    diaTipo: 'Día',
    frecuencia: 'Frecuencia',
    horaDesde: 'Desde',
    horaHasta: 'Hasta',
    duracionSegundos: 'Duración',
    material: 'Material',
    avisos: 'Avisos',
    ubicacionDigital: 'Ubicación',
    formatoDigital: 'Formato',
    fechaDesde: 'Desde',
    fechaHasta: 'Hasta',
    plataformaRedes: 'Plataforma',
    cantidad: 'Cantidad',
    tituloContenido: 'Título',
    eventoNombre: 'Evento',
    fechaEvento: 'Fecha',
    textoALeer: 'Texto a leer'
  };

  itemsByType = signal<Record<ItemTipoCodigo, ItemsControl[]>>({
    AIRE_SPOT: [],
    WEB_BANNER: [],
    REDES_PIEZA: [],
    PUBLINOTA: [],
    COBERTURA_EVENTO: [],
    PNT: []
  });
  countsByType = signal<Record<ItemTipoCodigo, number>>({
    AIRE_SPOT: 0,
    WEB_BANNER: 0,
    REDES_PIEZA: 0,
    PUBLINOTA: 0,
    COBERTURA_EVENTO: 0,
    PNT: 0
  });
  subtotalByType = signal<Record<ItemTipoCodigo, number>>({
    AIRE_SPOT: 0,
    WEB_BANNER: 0,
    REDES_PIEZA: 0,
    PUBLINOTA: 0,
    COBERTURA_EVENTO: 0,
    PNT: 0
  });

  programaSuggestions = signal<Programa[]>([]);
  materialSuggestions = signal<Material[]>([]);
  formatoDigitalSuggestions = signal<FormatoDigital[]>([]);
  ubicacionDigitalSuggestions = signal<UbicacionDigital[]>([]);
  lookupErrors = signal<Record<LookupField, boolean>>({
    programa: false,
    material: false,
    formatoDigital: false,
    ubicacionDigital: false
  });

  private rowLoadingCounts = signal<Record<number, number>>({});
  private lastQueries: Record<LookupField, string> = {
    programa: '',
    material: '',
    formatoDigital: '',
    ubicacionDigital: ''
  };
  private lookupCache = new Map<string, any>();
  private lookupRequests$ = new Subject<LookupRequest<any>>();

  constructor(
    private fb: FormBuilder,
    private programaService: ProgramaService,
    private materialService: MaterialService,
    private tipoMaterialService: TipoMaterialService,
    private formatoDigitalService: FormatoDigitalService,
    private ubicacionDigitalService: UbicacionDigitalService
  ) {
    this.lookupRequests$
      .pipe(
        groupBy((request) => request.field),
        mergeMap((group$) =>
          group$.pipe(
            debounceTime(250),
            switchMap((request) => {
              this.setRowLoading(request.rowIndex, true);
              this.lookupErrors.update((current) => ({ ...current, [request.field]: false }));
              this.lastQueries[request.field] = request.query;

              return request.fetch().pipe(
                map((resp: any) => ({
                  options: request.mapResponse(resp),
                  request
                })),
                catchError((error) => {
                  console.error(`Lookup error (${request.field})`, {
                    query: request.query,
                    rowIndex: request.rowIndex,
                    error
                  });
                  this.lookupErrors.update((current) => ({ ...current, [request.field]: true }));
                  return of({ options: [], request });
                }),
                finalize(() => this.setRowLoading(request.rowIndex, false))
              );
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ options, request }) => {
        request.suggestionsSignal.set(options);
        this.maybeAutoSelect(request.group, request.field, options);
      });
  }

  ngOnDestroy(): void {
    this.formArraySubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setFormArray(itemsFormArray: FormArray): void {
    if (this.itemsFormArray === itemsFormArray) {
      return;
    }
    this.formArraySubscription?.unsubscribe();
    this.itemsFormArray = itemsFormArray;
    this.refreshItems();

    this.formArraySubscription = this.itemsFormArray.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.refreshItems();
    });
  }

  setTipoProductoOptions(options: TipoProducto[]): void {
    this.tipoProductoOptions = options;
    this.syncTipoProductoControls();
    this.assignTipoProductoDefaults();
    this.refreshItems();
  }

  ensureTipoProductoForGroup(group: FormGroup): void {
    const tipoControl = group.get('tipoProducto');
    if (!tipoControl || tipoControl.value) {
      return;
    }
    const codigo = this.resolveTipoProductoCodigoFromGroup(group);
    if (!codigo) {
      return;
    }
    const tipoProducto = this.findTipoProductoByCodigo(codigo);
    if (tipoProducto) {
      tipoControl.setValue(tipoProducto, { emitEvent: false });
      this.applyTipoProductoValidators(group);
    }
  }

  addItem(tipoProductoCodigo?: ItemTipoCodigo | null): void {
    if (!this.itemsFormArray) return;
    const tipoProducto = this.findTipoProductoByCodigo(tipoProductoCodigo ?? null);
    const itemGroup = this.createItemGroup({
      ordenIndex: this.itemsFormArray.length,
      tipoProducto: tipoProducto || undefined
    });
    if (tipoProductoCodigo) {
      this.applyLastSelection(tipoProductoCodigo, itemGroup);
    }
    this.itemsFormArray.push(itemGroup);
    this.refreshItems();
  }

  removeItem(index: number): void {
    if (!this.itemsFormArray) return;
    const group = this.itemsFormArray.at(index) as FormGroup | null;
    const itemId = group?.get('id')?.value;
    if (itemId) {
      const numericId = Number(itemId);
      if (!Number.isNaN(numericId)) {
        this.deletedItemIds.add(numericId);
      }
    }
    this.itemsFormArray.removeAt(index);
    this.refreshItems();
  }

  getDeletedItemIds(): number[] {
    return Array.from(this.deletedItemIds.values());
  }

  clearDeletedItemIds(): void {
    this.deletedItemIds.clear();
  }

  /**
   * Refresca los subtotales por tipo de producto.
   * Llamar después de recalcular precios para mantener sincronizados
   * los valores en la UI (headers de sección).
   */
  refreshSubtotals(): void {
    this.refreshItems();
  }

  getSubtotal(itemGroup: FormGroup): number {
    const inputs = {
      subtotalManual: itemGroup.get('subtotalManual')?.value,
      subtotalPreview: itemGroup.get('subtotalPreview')?.value,
      subtotalSnapshot: itemGroup.get('subtotalSnapshot')?.value,
      subtotal: itemGroup.get('subtotal')?.value
    };
    const hasSource = Object.values(inputs).some((value) => value !== null && value !== undefined && value !== '');
    if (hasSource) {
      return resolveSubtotalFinal(inputs);
    }

    const avisos = Number(itemGroup.get('avisos')?.value || 0);
    const duracion = Number(itemGroup.get('duracionSegundos')?.value || 0);
    const costo = Number(itemGroup.get('costoSegundo')?.value || 0);
    const factor = Number(itemGroup.get('factorPrecio')?.value || 1);
    return avisos * duracion * costo * factor;
  }

  isInvalid(itemGroup: FormGroup, controlName: string): boolean {
    const control = itemGroup.get(controlName);
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  showHoraError(itemGroup: FormGroup): boolean {
    if (!itemGroup.hasError('horaRango')) {
      return false;
    }
    const desdeTouched = itemGroup.get('horaDesde')?.touched;
    const hastaTouched = itemGroup.get('horaHasta')?.touched;
    return !!(desdeTouched || hastaTouched);
  }

  isRowLoading(index: number): boolean {
    return (this.rowLoadingCounts()[index] || 0) > 0;
  }

  hasLookupError(field: LookupField): boolean {
    return !!this.lookupErrors()[field];
  }

  isRowComplete(itemGroup: FormGroup, tipoProductoCodigo: ItemTipoCodigo): boolean {
    const requiredFields = this.getRequiredFields(tipoProductoCodigo);
    return requiredFields.every((field) => {
      const control = itemGroup.get(field);
      if (!control) return false;
      const value = control.value;
      if (typeof value === 'boolean') return true;
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return control.valid;
    });
  }

  isRowInvalid(itemGroup: FormGroup, tipoProductoCodigo: ItemTipoCodigo): boolean {
    const requiredFields = this.getRequiredFields(tipoProductoCodigo);
    return requiredFields.some((field) => {
      const control = itemGroup.get(field);
      return !!(control && control.invalid && (control.touched || control.dirty));
    });
  }

  isDraftRow(itemGroup: FormGroup, tipoProductoCodigo: ItemTipoCodigo, index: number): boolean {
    const items = this.itemsByType()[tipoProductoCodigo] || [];
    const isLast = items.length > 0 && items[items.length - 1].index === index;
    if (!isLast) return false;
    const requiredFields = this.getRequiredFields(tipoProductoCodigo);
    const isEmpty = requiredFields.every((field) => {
      const control = itemGroup.get(field);
      const value = control?.value;
      return value === null || value === undefined || value === '';
    });
    return isEmpty && !itemGroup.dirty;
  }

  shouldConfirmDelete(itemGroup: FormGroup, tipoProductoCodigo: ItemTipoCodigo): boolean {
    if (this.isRowComplete(itemGroup, tipoProductoCodigo)) {
      return true;
    }
    return this.getSubtotal(itemGroup) > 0;
  }

  onMaterialSelect(itemGroup: FormGroup): void {
    const material = itemGroup.get('material')?.value as Material | string | null;
    if (!material || typeof material === 'string') {
      return;
    }

    if (material.costoDelSegundo !== undefined) {
      itemGroup.patchValue({ costoSegundo: material.costoDelSegundo }, { emitEvent: false });
    }

    this.applyDefaultDuracion(itemGroup);

    const tipoMaterialId = this.extractId(material.tipoMaterial);
    if (tipoMaterialId) {
      this.tipoMaterialService.getById(tipoMaterialId).subscribe({
        next: (tipoMaterial: any) => {
          const factor = tipoMaterial?.factorPrecio ?? itemGroup.get('factorPrecio')?.value ?? '1';
          itemGroup.patchValue({ factorPrecio: String(factor) }, { emitEvent: false });
        }
      });
    }
  }

  canApplyDefaultDuracion(itemGroup: FormGroup): boolean {
    const material = itemGroup.get('material')?.value as Material | string | null;
    if (!material || typeof material === 'string') {
      return false;
    }
    const d = material.duracion;
    return typeof d === 'number' && d >= 0;
  }

  applyDefaultDuracion(itemGroup: FormGroup): void {
    if (!this.canApplyDefaultDuracion(itemGroup)) {
      return;
    }
    const material = itemGroup.get('material')?.value as Material;
    const duracion = Number(material.duracion);
    const prev = itemGroup.get('duracionSegundos')?.value;
    itemGroup.patchValue({
      duracionSegundos: Number.isFinite(duracion) ? duracion : (prev ?? 0)
    }, { emitEvent: false });
  }

  searchProgramas(query: string, group: FormGroup, rowIndex: number): void {
    const params = new HttpParams().set('q', query).set('pagination', 'false');
    this.lookupRequests$.next({
      query,
      rowIndex,
      group,
      field: 'programa',
      suggestionsSignal: this.programaSuggestions,
      fetch: () => this.cachedLookup(`programas:${query}`, () => this.programaService.searchProgramas(params)),
      mapResponse: (resp) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        return members.map((programa: any) => ({
          ...programa,
          displayName: `${programa.codigo || ''} - ${programa.nombre || ''}`.trim()
        }));
      }
    });
  }

  searchMateriales(query: string, group: FormGroup, rowIndex: number): void {
    const params = new HttpParams().set('q', query).set('pagination', 'false');
    this.lookupRequests$.next({
      query,
      rowIndex,
      group,
      field: 'material',
      suggestionsSignal: this.materialSuggestions,
      fetch: () => this.cachedLookup(`materiales:${query}`, () => this.materialService.searchMaterials(params)),
      mapResponse: (resp) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        return members.map((material: any) => ({
          ...material,
          displayName: `${material.codigo || ''} - ${material.nombre || ''}`.trim()
        }));
      }
    });
  }

  searchFormatosDigitales(query: string, group: FormGroup, rowIndex: number): void {
    const params = new HttpParams()
      .set('q', query)
      .set('pagination', 'false')
      .set('active', 'true');
    this.lookupRequests$.next({
      query,
      rowIndex,
      group,
      field: 'formatoDigital',
      suggestionsSignal: this.formatoDigitalSuggestions,
      fetch: () => this.cachedLookup(`formatos:${query}`, () => this.formatoDigitalService.searchFormatos(params)),
      mapResponse: (resp) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        return members.map((formato: any) => ({
          ...formato,
          displayName: `${formato.codigo || ''} - ${formato.nombre || ''}`.trim()
        }));
      }
    });
  }

  searchUbicacionesDigitales(query: string, group: FormGroup, rowIndex: number): void {
    const params = new HttpParams()
      .set('q', query)
      .set('pagination', 'false')
      .set('active', 'true');
    this.lookupRequests$.next({
      query,
      rowIndex,
      group,
      field: 'ubicacionDigital',
      suggestionsSignal: this.ubicacionDigitalSuggestions,
      fetch: () => this.cachedLookup(`ubicaciones:${query}`, () => this.ubicacionDigitalService.searchUbicaciones(params)),
      mapResponse: (resp) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        return members.map((ubicacion: any) => ({
          ...ubicacion,
          displayName: `${ubicacion.codigo || ''} - ${ubicacion.nombre || ''}`.trim()
        }));
      }
    });
  }

  retryLookup(field: LookupField, group: FormGroup, rowIndex: number): void {
    const query = this.lastQueries[field];
    if (!query) return;
    if (field === 'programa') {
      this.searchProgramas(query, group, rowIndex);
    } else if (field === 'material') {
      this.searchMateriales(query, group, rowIndex);
    } else if (field === 'formatoDigital') {
      this.searchFormatosDigitales(query, group, rowIndex);
    } else if (field === 'ubicacionDigital') {
      this.searchUbicacionesDigitales(query, group, rowIndex);
    }
  }

  calculateDias(itemGroup: FormGroup, vigenciaDesde?: Date | null, vigenciaHasta?: Date | null): number {
    const desde = itemGroup.get('fechaDesde')?.value || vigenciaDesde;
    const hasta = itemGroup.get('fechaHasta')?.value || vigenciaHasta;
    if (!desde || !hasta) {
      return 0;
    }

    const desdeDate = new Date(desde);
    const hastaDate = new Date(hasta);
    if (isNaN(desdeDate.getTime()) || isNaN(hastaDate.getTime())) {
      return 0;
    }

    const diffMs = hastaDate.setHours(0, 0, 0, 0) - desdeDate.setHours(0, 0, 0, 0);
    if (diffMs < 0) {
      return 0;
    }

    return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
  }

  getDisplayName(value: any, fallback: string = '-'): string {
    if (!value) return fallback;
    if (typeof value === 'string') {
      const match = value.match(/\/(\d+)$/);
      return match ? `#${match[1]}` : value;
    }
    if (value.displayName) return value.displayName;
    if (value.codigo || value.nombre) {
      return `${value.codigo || ''} - ${value.nombre || ''}`.trim();
    }
    return fallback;
  }

  private refreshItems(): void {
    if (!this.itemsFormArray) {
      return;
    }
    const grouped: Record<ItemTipoCodigo, ItemsControl[]> = {
      AIRE_SPOT: [],
      WEB_BANNER: [],
      REDES_PIEZA: [],
      PUBLINOTA: [],
      COBERTURA_EVENTO: [],
      PNT: []
    };
    this.itemsFormArray.controls.forEach((control, index) => {
      const group = control as FormGroup;
      const tipo = group.get('tipoProducto')?.value;
      const codigo = tipo?.codigo as ItemTipoCodigo | undefined;
      if (codigo && grouped[codigo]) {
        grouped[codigo].push({ group, index });
      }
    });
    this.itemsByType.set(grouped);
    this.countsByType.set({
      AIRE_SPOT: grouped.AIRE_SPOT.length,
      WEB_BANNER: grouped.WEB_BANNER.length,
      REDES_PIEZA: grouped.REDES_PIEZA.length,
      PUBLINOTA: grouped.PUBLINOTA.length,
      COBERTURA_EVENTO: grouped.COBERTURA_EVENTO.length,
      PNT: grouped.PNT.length
    });
    this.subtotalByType.set({
      AIRE_SPOT: grouped.AIRE_SPOT.reduce((acc, item) => acc + this.getSubtotal(item.group), 0),
      WEB_BANNER: grouped.WEB_BANNER.reduce((acc, item) => acc + this.getSubtotal(item.group), 0),
      REDES_PIEZA: grouped.REDES_PIEZA.reduce((acc, item) => acc + this.getSubtotal(item.group), 0),
      PUBLINOTA: grouped.PUBLINOTA.reduce((acc, item) => acc + this.getSubtotal(item.group), 0),
      COBERTURA_EVENTO: grouped.COBERTURA_EVENTO.reduce((acc, item) => acc + this.getSubtotal(item.group), 0),
      PNT: grouped.PNT.reduce((acc, item) => acc + this.getSubtotal(item.group), 0)
    });
    this.captureLastSelections();
  }

  private captureLastSelections(): void {
    const aireItems = this.itemsByType().AIRE_SPOT || [];
    const lastAire = [...aireItems].reverse().find((item) => this.isRowComplete(item.group, 'AIRE_SPOT'));
    if (lastAire) {
      this.lastSelections.AIRE_SPOT = {
        programa: lastAire.group.get('programa')?.value ?? null,
        diaTipo: lastAire.group.get('diaTipo')?.value ?? null,
        frecuencia: lastAire.group.get('frecuencia')?.value ?? null
      };
    }
  }

  private applyLastSelection(tipoProductoCodigo: ItemTipoCodigo, itemGroup: FormGroup): void {
    if (tipoProductoCodigo !== 'AIRE_SPOT') {
      return;
    }
    const last = this.lastSelections.AIRE_SPOT;
    if (!last) return;
    itemGroup.patchValue({
      programa: last.programa ?? null,
      diaTipo: last.diaTipo ?? null,
      frecuencia: last.frecuencia ?? null
    }, { emitEvent: false });
  }

  private maybeAutoSelect(itemGroup: FormGroup, field: LookupField, options: any[]): void {
    if (options.length !== 1) return;
    const controlName = this.getControlNameByField(field);
    if (!controlName) return;
    const control = itemGroup.get(controlName);
    if (control && !control.value) {
      control.setValue(options[0], { emitEvent: false });
    }
  }

  private getControlNameByField(field: LookupField): string | null {
    if (field === 'programa') return 'programa';
    if (field === 'material') return 'material';
    if (field === 'formatoDigital') return 'formatoDigital';
    if (field === 'ubicacionDigital') return 'ubicacionDigital';
    return null;
  }

  private setRowLoading(rowIndex: number, isLoading: boolean): void {
    const current = { ...this.rowLoadingCounts() };
    const nextCount = (current[rowIndex] || 0) + (isLoading ? 1 : -1);
    if (nextCount <= 0) {
      delete current[rowIndex];
    } else {
      current[rowIndex] = nextCount;
    }
    this.rowLoadingCounts.set(current);
  }

  private cachedLookup<T>(cacheKey: string, fetchFn: () => any) {
    if (!this.lookupCache.has(cacheKey)) {
      this.lookupCache.set(cacheKey, fetchFn().pipe(shareReplay(1)));
    }
    return this.lookupCache.get(cacheKey);
  }

  createItemGroup(item?: any): FormGroup {
    const clientRowId = item?.clientRowId ?? `row_${++this.rowIdCounter}`;

    // Determinar el valor por defecto de cantidad según el tipo de producto
    const tipoProductoCodigo = this.getTipoProductoCodigo(item?.tipoProducto);
    const requiresCantidad = tipoProductoCodigo && ['WEB_BANNER', 'REDES_PIEZA', 'PUBLINOTA', 'COBERTURA_EVENTO', 'PNT'].includes(tipoProductoCodigo);
    const cantidadValue = item?.cantidad ?? (requiresCantidad ? 1 : null);

    const group = this.fb.group({
      id: [item?.id || null],
      clientRowId: [clientRowId],
      tipoProducto: [this.normalizeAutoCompleteEntity(item?.tipoProducto) || null, [Validators.required]],
      programa: [this.normalizeAutoCompleteEntity(item?.programa) || null],
      material: [this.normalizeAutoCompleteEntity(item?.material) || null],
      diaTipo: [item?.diaTipo ?? null],
      frecuencia: [item?.frecuencia ?? null],
      horaDesde: [this.parseTimeToDate(item?.horaDesde)],
      horaHasta: [this.parseTimeToDate(item?.horaHasta)],
      duracionSegundos: [item?.duracionSegundos ?? null, [Validators.min(1)]],
      avisos: [item?.avisos ?? 0, [Validators.min(0)]],
      cantidad: [cantidadValue, [Validators.min(1)]],
      bonificado: [item?.bonificado ?? false],
      motivoBonificacion: [item?.motivoBonificacion ?? null],
      costoSegundo: [item?.costoSegundo ?? null],
      factorPrecio: [item?.factorPrecio ?? null],
      subtotal: [item?.subtotal ?? null],
      formatoDigital: [this.normalizeAutoCompleteEntity(item?.formatoDigital) || null],
      ubicacionDigital: [this.normalizeAutoCompleteEntity(item?.ubicacionDigital) || null],
      fechaDesde: [item?.fechaDesde ? new Date(item.fechaDesde) : null],
      fechaHasta: [item?.fechaHasta ? new Date(item.fechaHasta) : null],
      plataformaRedes: [item?.plataformaRedes ?? null],
      tituloContenido: [item?.tituloContenido ?? null],
      eventoNombre: [item?.eventoNombre ?? null],
      fechaEvento: [item?.fechaEvento ? new Date(item.fechaEvento) : null],
      textoALeer: [item?.textoALeer ?? null],
      tarifaDetalleId: [this.extractId(item?.tarifaDetalle) ?? null],
      tarifaDetallePreviewId: [item?.tarifaDetallePreviewId ?? null],
      precioUnitarioSnapshot: [item?.precioUnitarioSnapshot ?? null],
      precioUnitarioPreview: [item?.precioUnitarioPreview ?? null],
      subtotalSnapshot: [item?.subtotalSnapshot ?? null],
      subtotalPreview: [item?.subtotalPreview ?? null],
      subtotalManual: [item?.subtotalManual ?? null, [Validators.min(0)]],
      subtotalFinal: [item?.subtotalFinal ?? null],
      unidadSnapshot: [item?.unidadSnapshot ?? null],
      unidadTarifacionPreview: [item?.unidadTarifacionPreview ?? null],
      factorSnapshot: [item?.factorSnapshot ?? null],
      metadataSnapshot: [item?.metadataSnapshot ?? null],
      duracionSegundosPreview: [item?.duracionSegundosPreview ?? null],
      pricingStatus: [item?.pricingStatus ?? null],
      pricingTrace: [item?.pricingTrace ?? null],
      pricingError: [item?.pricingError ?? null],
      ordenIndex: [item?.ordenIndex ?? 0],
      observacion: [item?.observacion ?? null]
    }, { validators: this.horaRangoValidator });

    this.applyTipoProductoValidators(group);
    group.get('tipoProducto')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyTipoProductoValidators(group);
    });

    return group;
  }

  private applyTipoProductoValidators(group: FormGroup): void {
    const tipoProducto = group.get('tipoProducto')?.value;
    const codigo = this.getTipoProductoCodigo(tipoProducto);

    const program = group.get('programa');
    const material = group.get('material');
    const diaTipo = group.get('diaTipo');
    const frecuencia = group.get('frecuencia');
    const horaDesde = group.get('horaDesde');
    const horaHasta = group.get('horaHasta');
    const duracionSegundos = group.get('duracionSegundos');
    const avisos = group.get('avisos');
    const cantidad = group.get('cantidad');
    const ubicacion = group.get('ubicacionDigital');
    const formato = group.get('formatoDigital');
    const fechaDesde = group.get('fechaDesde');
    const fechaHasta = group.get('fechaHasta');
    const plataforma = group.get('plataformaRedes');
    const titulo = group.get('tituloContenido');
    const evento = group.get('eventoNombre');
    const fechaEvento = group.get('fechaEvento');
    const textoALeer = group.get('textoALeer');

    program?.clearValidators();
    diaTipo?.clearValidators();
    frecuencia?.clearValidators();
    horaDesde?.clearValidators();
    horaHasta?.clearValidators();
    duracionSegundos?.clearValidators();
    material?.clearValidators();
    avisos?.clearValidators();
    cantidad?.clearValidators();
    ubicacion?.clearValidators();
    formato?.clearValidators();
    fechaDesde?.clearValidators();
    fechaHasta?.clearValidators();
    plataforma?.clearValidators();
    titulo?.clearValidators();
    evento?.clearValidators();
    fechaEvento?.clearValidators();
    textoALeer?.clearValidators();

    if (codigo === 'AIRE_SPOT') {
      program?.setValidators([Validators.required]);
      diaTipo?.setValidators([Validators.required]);
      frecuencia?.setValidators([Validators.required]);
      horaDesde?.setValidators([Validators.required]);
      horaHasta?.setValidators([Validators.required]);
      duracionSegundos?.setValidators([Validators.required, Validators.min(1)]);
      material?.setValidators([Validators.required]);
      avisos?.setValidators([Validators.required, Validators.min(0)]);
    } else if (frecuencia && frecuencia.value) {
      frecuencia.setValue(null, { emitEvent: false });
    } else if (codigo === 'WEB_BANNER') {
      ubicacion?.setValidators([Validators.required, selectedEntityValidator()]);
      formato?.setValidators([Validators.required, selectedEntityValidator()]);
      fechaDesde?.setValidators([Validators.required]);
      fechaHasta?.setValidators([Validators.required]);
      cantidad?.setValidators([Validators.required, Validators.min(1)]);
    } else if (codigo === 'REDES_PIEZA') {
      plataforma?.setValidators([Validators.required]);
      formato?.setValidators([Validators.required]);
      cantidad?.setValidators([Validators.required, Validators.min(1)]);
    } else if (codigo === 'PUBLINOTA') {
      titulo?.setValidators([Validators.required]);
      cantidad?.setValidators([Validators.required, Validators.min(1)]);
    } else if (codigo === 'COBERTURA_EVENTO') {
      evento?.setValidators([Validators.required]);
      fechaEvento?.setValidators([Validators.required]);
      cantidad?.setValidators([Validators.required, Validators.min(1)]);
    } else if (codigo === 'PNT') {
      textoALeer?.setValidators([Validators.required, Validators.maxLength(2000)]);
      cantidad?.setValidators([Validators.required, Validators.min(1)]);
    }

    program?.updateValueAndValidity({ emitEvent: false });
    diaTipo?.updateValueAndValidity({ emitEvent: false });
    frecuencia?.updateValueAndValidity({ emitEvent: false });
    horaDesde?.updateValueAndValidity({ emitEvent: false });
    horaHasta?.updateValueAndValidity({ emitEvent: false });
    duracionSegundos?.updateValueAndValidity({ emitEvent: false });
    material?.updateValueAndValidity({ emitEvent: false });
    avisos?.updateValueAndValidity({ emitEvent: false });
    cantidad?.updateValueAndValidity({ emitEvent: false });
    ubicacion?.updateValueAndValidity({ emitEvent: false });
    formato?.updateValueAndValidity({ emitEvent: false });
    fechaDesde?.updateValueAndValidity({ emitEvent: false });
    fechaHasta?.updateValueAndValidity({ emitEvent: false });
    plataforma?.updateValueAndValidity({ emitEvent: false });
    titulo?.updateValueAndValidity({ emitEvent: false });
    evento?.updateValueAndValidity({ emitEvent: false });
    fechaEvento?.updateValueAndValidity({ emitEvent: false });
    textoALeer?.updateValueAndValidity({ emitEvent: false });
  }

  getRequiredFields(tipoProductoCodigo: ItemTipoCodigo): string[] {
    return this.requiredFieldsByTipo[tipoProductoCodigo] ?? [];
  }

  getRequiredFieldLabels(tipoProductoCodigo: ItemTipoCodigo): string[] {
    return this.getRequiredFields(tipoProductoCodigo).map((field) => this.requiredFieldLabels[field] ?? field);
  }

  resolveItemTypeFromGroup(group: FormGroup): ItemTipoCodigo | null {
    const tipoProducto = group.get('tipoProducto')?.value;
    const codigo = this.getTipoProductoCodigo(tipoProducto);
    return codigo ?? this.resolveTipoProductoCodigoFromGroup(group);
  }

  private getTipoProductoCodigo(value: unknown): ItemTipoCodigo | undefined {
    if (!value) {
      return undefined;
    }
    if (typeof value === 'object' && 'codigo' in value) {
      return (value as { codigo?: string }).codigo as ItemTipoCodigo | undefined;
    }
    return undefined;
  }

  private findTipoProductoByCodigo(codigo: ItemTipoCodigo | null): TipoProducto | null {
    if (!codigo) return null;
    return this.tipoProductoOptions.find(tipo => tipo.codigo === codigo) || null;
  }

  private syncTipoProductoControls(): void {
    if (!this.itemsFormArray) return;
    this.itemsFormArray.controls.forEach(control => {
      const tipoControl = control.get('tipoProducto');
      if (!tipoControl) {
        return;
      }
      const value = tipoControl.value;
      if (typeof value === 'string' || typeof value === 'number') {
        const id = this.extractId(value);
        const match = this.tipoProductoOptions.find(tipo => this.extractId(tipo) === id);
        if (match) {
          tipoControl.setValue(match, { emitEvent: false });
        }
        return;
      }
      if (value && typeof value === 'object' && value.codigo && !value.id) {
        const match = this.tipoProductoOptions.find(tipo => tipo.codigo === value.codigo);
        if (match) {
          tipoControl.setValue(match, { emitEvent: false });
        }
      }
    });
  }

  private assignTipoProductoDefaults(): void {
    if (!this.itemsFormArray) return;
    this.itemsFormArray.controls.forEach(control => {
      const group = control as FormGroup;
      const tipoControl = group.get('tipoProducto');
      if (!tipoControl || tipoControl.value) {
        return;
      }
      const codigo = this.resolveTipoProductoCodigoFromGroup(group);
      if (!codigo) {
        return;
      }
      const tipoProducto = this.findTipoProductoByCodigo(codigo);
      if (tipoProducto) {
        tipoControl.setValue(tipoProducto, { emitEvent: false });
        this.applyTipoProductoValidators(group);
      }
    });
  }

  private resolveTipoProductoCodigoFromGroup(group: FormGroup): ItemTipoCodigo | null {
    if (group.get('programa')?.value || group.get('material')?.value || group.get('diaTipo')?.value) {
      return 'AIRE_SPOT';
    }
    if (group.get('ubicacionDigital')?.value || group.get('fechaDesde')?.value || group.get('fechaHasta')?.value) {
      return 'WEB_BANNER';
    }
    if (group.get('plataformaRedes')?.value) {
      return 'REDES_PIEZA';
    }
    if (group.get('formatoDigital')?.value) {
      return 'WEB_BANNER';
    }
    if (group.get('tituloContenido')?.value) {
      return 'PUBLINOTA';
    }
    if (group.get('eventoNombre')?.value || group.get('fechaEvento')?.value) {
      return 'COBERTURA_EVENTO';
    }
    if (group.get('textoALeer')?.value) {
      return 'PNT';
    }
    return null;
  }

  private normalizeAutoCompleteEntity(value: any): any {
    if (!value || typeof value === 'string' || typeof value === 'number') {
      return value;
    }

    if (value.displayName) {
      return value;
    }

    const codigo = value.codigo || '';
    const nombre = value.nombre || '';
    const displayName = `${codigo} - ${nombre}`.trim();

    return {
      ...value,
      displayName: displayName || value.razonSocial || value.nombreFantasia || value.cuit || `#${value.id ?? ''}`
    };
  }

  /**
   * Parsea un valor de hora a Date para el DatePicker.
   * Soporta múltiples formatos:
   * - HH:mm o HH:mm:ss (nuevo formato desde backend)
   * - ISO completo como 1970-01-01T07:00:00+00:00 (formato legacy)
   * - Date objects
   * 
   * IMPORTANTE: Para strings de hora, crea un Date en hora LOCAL para
   * que el DatePicker lo muestre correctamente. El matching de tarifas
   * usa timeToMinutes() que maneja UTC correctamente.
   */
  private parseTimeToDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      
      // Caso: ISO completo con T (formato legacy: "1970-01-01T07:00:00+00:00")
      // Usar Date.parse que maneja timezone correctamente
      if (trimmed.includes('T')) {
        const date = new Date(trimmed);
        if (!isNaN(date.getTime())) {
          // Para fechas 1970-01-01 (horas de tarifa), crear nuevo Date
          // con la hora en tiempo local para que el DatePicker muestre correctamente
          const hours = date.getUTCHours();
          const minutes = date.getUTCMinutes();
          const seconds = date.getUTCSeconds();
          const localDate = new Date(1970, 0, 1, hours, minutes, seconds);
          return localDate;
        }
        return null;
      }
      
      // Caso: HH:mm o HH:mm:ss (nuevo formato desde backend)
      const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
      const match = trimmed.match(timeRegex);
      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const seconds = match[3] ? parseInt(match[3], 10) : 0;
        if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
          // Crear Date en hora local para que DatePicker muestre correcto
          return new Date(1970, 0, 1, hours, minutes, seconds);
        }
      }
      
      return null;
    }
    return null;
  }

  private horaRangoValidator(group: FormGroup): { [key: string]: boolean } | null {
    const desde = group.get('horaDesde')?.value;
    const hasta = group.get('horaHasta')?.value;
    if (!desde || !hasta) {
      return null;
    }

    const desdeDate = desde instanceof Date ? desde : new Date(`1970-01-01T${desde}`);
    const hastaDate = hasta instanceof Date ? hasta : new Date(`1970-01-01T${hasta}`);

    if (isNaN(desdeDate.getTime()) || isNaN(hastaDate.getTime())) {
      return null;
    }

    return hastaDate >= desdeDate ? null : { horaRango: true };
  }

  private extractId(value: any): string | null {
    if (!value) return null;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'string') {
      const match = value.match(/\/(\d+)$/);
      return match ? match[1] : null;
    }
    if (value.id) return value.id.toString();
    return null;
  }
}
